import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Dna, TrendingUp, TrendingDown, Minus, Shield, Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API_BASE as API } from '../config';
function headers() { return { Authorization: `Bearer ${localStorage.getItem("token")}` }; }

const STRENGTH_COLORS = { strong: "#00FF7F", neutral: "#FFD700", weak: "#FF4500" };
const STRENGTH_ICONS = { strong: TrendingUp, neutral: Minus, weak: TrendingDown };

export default function StrategyDNAPage() {
  const [strategies, setStrategies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dna, setDna] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);

  const fetchBatch = useCallback(async () => {
    setBatchLoading(true);
    try {
      const r = await axios.get(`${API}/api/strategy-dna-batch?limit=10`, { headers: headers() });
      setStrategies(r.data.strategies || []);
    } catch (e) { /* ignore */ }
    setBatchLoading(false);
  }, []);

  useEffect(() => { fetchBatch(); }, [fetchBatch]);

  const analyzeSingle = async (name) => {
    setLoading(true);
    setSelected(name);
    try {
      const r = await axios.get(`${API}/api/strategy-dna/${encodeURIComponent(name)}?days=1825`, { headers: headers() });
      setDna(r.data);
      toast.success(`DNA analysis complete for ${name}`);
      fetchBatch(); // refresh batch to include cached result
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    }
    setLoading(false);
  };

  const chartData = dna?.regimes?.map((r) => ({
    name: r.name.replace(/\//g, "/\n"),
    pnl: r.pnl_pct,
    trades: r.trades,
    wr: r.win_rate,
    color: r.color,
    strength: r.strength,
  })) || [];

  return (
    <div className="min-h-screen bg-black text-white p-4 space-y-4" data-testid="strategy-dna-page">
      <div className="flex items-center justify-between" data-testid="dna-header">
        <div>
          <h1 className="text-xl font-black tracking-wider flex items-center gap-2">
            <Dna className="w-5 h-5 text-violet-400" />
            STRATEGY DNA
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Regime Performance Analysis — Where each strategy thrives or fails
          </p>
        </div>
        <Button onClick={fetchBatch} variant="outline" className="h-8 px-3 text-xs border-zinc-700 text-zinc-400" data-testid="refresh-dna-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${batchLoading ? "animate-spin" : ""}`} /> REFRESH
        </Button>
      </div>

      <div className="grid grid-cols-[300px_1fr] gap-4 h-[calc(100vh-130px)]">
        {/* Strategy List */}
        <div className="space-y-2 overflow-y-auto" data-testid="dna-strategy-list">
          <div className="text-xs font-bold text-zinc-400 tracking-wider pl-1 mb-1">TOP STRATEGIES</div>
          {strategies.map((s) => {
            const isAnalyzed = s.regimes && s.regimes.length > 0;
            const isActive = selected === s.strategy_name;
            return (
              <button
                key={s.strategy_name}
                onClick={() => isAnalyzed ? setDna(s) || setSelected(s.strategy_name) : analyzeSingle(s.strategy_name)}
                className={`w-full text-left p-2.5 rounded-sm border transition-all ${
                  isActive ? "bg-violet-950/30 border-violet-700/50" : "bg-zinc-900/60 border-zinc-800/60 hover:border-zinc-700"
                }`}
                data-testid={`dna-strategy-${s.strategy_name}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-zinc-300 truncate max-w-[180px]">{s.strategy_name}</span>
                  {isAnalyzed ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 text-[9px]">ANALYZED</Badge>
                  ) : (
                    <Badge className="bg-zinc-800 text-zinc-500 text-[9px]">CLICK TO ANALYZE</Badge>
                  )}
                </div>
                {isAnalyzed && s.total_pnl_pct !== undefined && (
                  <div className="mt-1 text-[10px] font-mono text-zinc-500">
                    Total: <span className={s.total_pnl_pct >= 0 ? "text-emerald-400" : "text-red-400"}>{s.total_pnl_pct > 0 ? "+" : ""}{s.total_pnl_pct}%</span>
                  </div>
                )}
              </button>
            );
          })}
          {strategies.length === 0 && !batchLoading && (
            <div className="text-zinc-600 text-sm text-center py-6">No strategies in registry</div>
          )}
          {batchLoading && (
            <div className="text-zinc-600 text-sm text-center py-6 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          )}
        </div>

        {/* DNA Analysis Panel */}
        <div className="space-y-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full text-zinc-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Analyzing {selected}...
            </div>
          ) : dna ? (
            <>
              {/* Strategy Header */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-bold text-violet-400">{dna.strategy_name}</span>
                  <span className="text-xs text-zinc-500 ml-2 font-mono">{dna.symbol} {dna.days}d</span>
                </div>
                <span className={`text-lg font-mono font-black ${(dna.total_pnl_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {(dna.total_pnl_pct || 0) > 0 ? "+" : ""}{(dna.total_pnl_pct || 0)}%
                </span>
              </div>

              {/* Regime P&L Chart */}
              <Card className="bg-zinc-950 border-zinc-800" data-testid="dna-chart">
                <CardContent className="p-3" style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                      <XAxis dataKey="name" stroke="#555" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#555" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 4, fontSize: 11 }}
                        formatter={(v, name) => [name === "pnl" ? `${v}%` : v, name === "pnl" ? "P&L" : name]}
                      />
                      <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, i) => (
                          <Cell key={i} fill={entry.pnl >= 0 ? "#00FF7F" : "#FF4500"} fillOpacity={0.8} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Regime Detail Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2" data-testid="dna-regime-cards">
                {(dna.regimes || []).map((r) => {
                  const Icon = STRENGTH_ICONS[r.strength] || Minus;
                  return (
                    <Card key={r.regime_id} className="bg-zinc-950 border-zinc-800/60">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color }} />
                            <span className="text-[11px] font-bold text-zinc-300">{r.name}</span>
                          </div>
                          <Icon className="w-3.5 h-3.5" style={{ color: STRENGTH_COLORS[r.strength] }} />
                        </div>
                        <div className={`text-lg font-mono font-black ${r.pnl_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {r.pnl_pct > 0 ? "+" : ""}{r.pnl_pct}%
                        </div>
                        <div className="mt-1.5 space-y-0.5 text-[10px] font-mono text-zinc-500">
                          <div>Trades: <span className="text-zinc-400">{r.trades}</span> ({r.wins}W / {r.losses}L)</div>
                          <div>Win Rate: <span className={r.win_rate >= 50 ? "text-emerald-400" : "text-orange-400"}>{r.win_rate}%</span></div>
                          <div>Candles: <span className="text-zinc-400">{r.candles_in_regime}</span></div>
                        </div>
                        <div className="mt-1.5">
                          <Badge className={`text-[9px] ${r.strength === "strong" ? "bg-emerald-500/20 text-emerald-400" : r.strength === "weak" ? "bg-red-500/20 text-red-400" : "bg-zinc-800 text-zinc-400"}`}>
                            {r.strength.toUpperCase()}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-zinc-600 text-sm">
              <Shield className="w-5 h-5 mr-2" /> Select a strategy to analyze its DNA
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
